import React, { ReactNode } from 'react';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Location, State } from 'history';
import { InputProps } from '@bizapp-lp/pjr/organisms/InputFormType';

interface StepInfo {
  stepNumber: number;
  withCredential: boolean;
  nextStepIds: string[];
  errorPage: boolean;
  genStep: (stepProps: StepProps) => ReactNode;
}

interface StepDict {
  [stepId: string]: StepInfo;
}
export interface Workflow {
  steps: StepDict;
  totalStepQuantity: number;
  errorPageLink: string;
  stepPageLink: string;
  confirmCookieApi: string;
}

export interface MultiStepFormContainerGroupProps {
  className?: string;
  apiBaseUrl: string;
  heading?: string | JSX.Element;
  workflow: Workflow;

  inputProps: {
    [inputPropsList: string]: InputProps[];
  };
}

export const TIMEOUT_MSEC = 30 * 1000;

export type MultiStepFormState = {
  valid: boolean;
  inquiryToken: string;
};

export const timeout = <T extends any>(
  promise: Promise<T>,
  milliseconds: number,
): Promise<T> => {
  let timer: number;
  return Promise.race([
    promise,
    new Promise<T>((_resolve, reject) => {
      timer = window.setTimeout(reject, milliseconds);
    }),
  ]).finally(() => window.clearTimeout(timer));
};

const waitMsec = (milliseconds: number): Promise<void> => {
  let timer: number;
  return new Promise<void>((resolve, _) => {
    timer = window.setTimeout(resolve, milliseconds);
  }).finally(() => {
    window.clearTimeout(timer);
  });
};

const confirmCookie = async (
  baseUrl: string,
  confirmCookieApi: string,
): Promise<ConfirmCookieResponse> => {
  const method = 'POST';
  const url = `${baseUrl}${confirmCookieApi}`;

  const res = await fetch(url, {
    method: method,
    credentials: 'include',
  });
  if (res.status === 200) {
    return (await res.json()) as ConfirmCookieResponse;
  } else {
    throw Error('Confirm Cookie Failed');
  }
};

interface ConfirmCookieResponse {
  session: string;
}

const confirmCookieWithRetry = async (
  baseUrl: string,
  confirmCookieApi: string,
  times: number,
  waitRetryMsec: number,
): Promise<ConfirmCookieResponse> => {
  for (let i = 0; i < times; i++) {
    try {
      return await confirmCookie(baseUrl, confirmCookieApi);
    } catch (e) {
      await waitMsec(waitRetryMsec);
    }
  }
  throw Error(`Confirm Cookie Failed after Retry ${times} Times`);
};

type ActiveStepInfo = {
  stepNumber: number;
  sessionMode: SessionMode;
  stepId: string;
};

const MultiStepFormContainerGroup: React.FC<MultiStepFormContainerGroupProps> = ({
  className,
  apiBaseUrl,
  heading,
  workflow,
  inputProps,
}) => {
  const navigate = useNavigate();
  const location = useLocation() as Location<MultiStepFormState>;
  const validState = location.state?.valid ?? false;

  // page transition handling
  const getCrrStepNumberFromUrl = React.useCallback(() => {
    const searchParams = createSearchParams(location.search);
    const rawStep = parseInt(searchParams.get('step') ?? '');
    return isNaN(rawStep) ? 0 : rawStep - 1;
  }, [location]);
  const stepIdOfSessionMode = React.useCallback(
    (
      stepNumber: number,
      sessionMode: SessionMode,
      steps: StepInfo[],
    ): string => {
      switch (sessionMode) {
        case 'InSession':
          return `${stepNumber}-C`;
        case 'NotInSession':
          return `${stepNumber}-N`;
        case 'Checking':
          const step = steps.find((step) => step.stepNumber === stepNumber);
          if (step)
            return stepIdOfSessionMode(
              step.stepNumber,
              step.withCredential ? 'InSession' : 'NotInSession',
              [],
            );
          else {
            return 'NotFound';
          }
        default:
          return 'NotFound';
      }
    },
    [],
  );
  // initial active step info according to step parameter
  const initStepNumber = getCrrStepNumberFromUrl();
  const initSessionMode = 'Checking';
  const initStepInfo: ActiveStepInfo = {
    stepNumber: initStepNumber,
    sessionMode: initSessionMode,
    stepId: stepIdOfSessionMode(
      initStepNumber,
      initSessionMode,
      Object.values(workflow.steps),
    ),
  };
  const [activeStepInfo, setActiveStepInfo] = React.useState(initStepInfo);

  // update active step info when step parameter changes
  React.useEffect(() => {
    const stepNumber = getCrrStepNumberFromUrl();
    setActiveStepInfo({
      stepNumber: stepNumber,
      sessionMode: 'Checking',
      stepId: stepIdOfSessionMode(
        stepNumber,
        'Checking',
        Object.values(workflow.steps),
      ),
    });

    // trigger credential check when step number updates
    const checkSessionMode = async () => {
      try {
        const sessionRes = await timeout(
          confirmCookieWithRetry(
            apiBaseUrl,
            workflow.confirmCookieApi,
            3,
            3 * 1000,
          ),
          TIMEOUT_MSEC,
        );
        const isSessionMode = sessionRes.session === 'true';
        setActiveStepInfo((prevState) => {
          return {
            ...prevState,
            sessionMode: isSessionMode ? 'InSession' : 'NotInSession',
          };
        });
      } catch (e) {
        setActiveStepInfo((prevState) => {
          return {
            ...prevState,
            sessionMode: 'NotInSession',
          };
        });
      }
    };
    checkSessionMode();
  }, [getCrrStepNumberFromUrl, stepIdOfSessionMode, workflow, apiBaseUrl]);

  const stepIdOf = React.useCallback(
    (stepNumber: number, withCredential: boolean) => {
      return stepIdOfSessionMode(
        stepNumber,
        withCredential ? 'InSession' : 'NotInSession',
        [],
      );
    },
    [stepIdOfSessionMode],
  );

  const targetStepAvailable = (crrStep: string, targetStep: string) =>
    workflow.steps[crrStep].nextStepIds.includes(targetStep);

  React.useEffect(() => {
    const isStepAvailable = (stepId: string) => !!workflow.steps[stepId];
    if (!isStepAvailable(activeStepInfo.stepId) && !validState) {
      navigate(workflow.errorPageLink, { replace: true });
    }
  }, [workflow, validState, navigate, activeStepInfo]);

  const toStep = (
    targetStepNumber: number,
    withCredential: boolean,
    options?: {
      replace?: boolean;
      state?: State;
    },
  ) => {
    const targetStepId = stepIdOf(targetStepNumber, withCredential);
    if (targetStepAvailable(activeStepInfo.stepId, targetStepId)) {
      if (workflow.steps[targetStepId]?.errorPage) {
        navigate(workflow.errorPageLink, { replace: true });
      }
      navigate(
        `${workflow.stepPageLink}?step=${targetStepNumber + 1}`,
        options ?? {},
      );
      window['scrollTo']({ top: 0, behavior: 'smooth' });
    } else {
      navigate(workflow.errorPageLink, { replace: true });
    }
  };

  return (
    <>
      {workflow.steps[activeStepInfo.stepId]?.genStep({
        className: className,
        heading: heading,
        apiBaseUrl: apiBaseUrl,
        totalStepQuantity: workflow.totalStepQuantity,
        // activeStepId: activeStepId,
        activeStep: activeStepInfo.stepNumber,
        sessionMode: activeStepInfo.sessionMode,
        toStep: toStep,
        inputProps: inputProps,
      })}
    </>
  );
};

export type SessionMode = 'InSession' | 'NotInSession' | 'Checking';

export interface StepProps {
  className?: string;
  heading?: string | JSX.Element;
  apiBaseUrl: string;

  totalStepQuantity: number;
  activeStep: number;
  sessionMode: SessionMode;
  toStep: (
    targetStepNumber: number,
    withCredential: boolean,
    options?: {
      replace?: boolean;
      state?: State;
    },
  ) => void;

  inputProps?: {
    [inputPropsList: string]: InputProps[];
  };
}

export default MultiStepFormContainerGroup;
